<template>
  <div class="min-vh-100 d-flex justify-content-center align-items-center bg-light-gray">
    <div class="card shadow-sm border-0 rounded-4 overflow-hidden" style="max-width: 420px; width: 100%;">
      <div class="card-body p-5">
        <div class="text-center mb-4">
          <div class="d-inline-flex align-items-center justify-content-center gap-2 mb-2">
            <i class="fa-solid fa-lock text-theme fs-2" />
            <h2 class="fw-bold text-dark m-0" style="letter-spacing: -0.5px;">
              {{ $ln($brand) }}
            </h2>
          </div>
          <p class="text-muted small">
            {{ $appName }} · {{ $t('Restaurant Management System') }}
          </p>
        </div>

        <h5 class="text-center mb-3 fw-bold text-secondary">
          {{ $t('Set a New Password') }}
        </h5>

        <!-- โดนรีเซ็ตมา = บังคับตั้งใหม่ ห้ามข้าม (api/docs/password-reset.md §5.2) -->
        <div
          v-if="forced"
          class="alert alert-warning d-flex align-items-center p-2 mb-3"
          style="font-size: 14px;"
          role="alert"
        >
          <i class="fa-solid fa-triangle-exclamation me-2 ms-1" />
          <div>
            {{ $t('Your password was reset by the owner - please set your own password before continuing') }}
          </div>
        </div>

        <div
          v-if="failedMessage"
          class="alert alert-danger d-flex align-items-center p-1 mb-3"
          style="font-size: 14px;"
          role="alert"
        >
          <i class="fa-solid fa-circle-exclamation me-2 ms-1" />
          <div>
            {{ $t(failedMessage) }}
          </div>
        </div>

        <!--
          โดนรีเซ็ตมา = ไม่ต้องถามรหัสเดิมซ้ำ เพิ่งพิมพ์รหัสชั่วคราวตอน login เมื่อครู่
          token ที่ถืออยู่คือหลักฐานว่ารู้รหัสนั้นแล้ว (BE ผ่อนกฎให้เหมือนกัน — ChangePasswordMiddleware)
        -->
        <div class="mb-3" v-if="!forced">
          <label
            for="current_password"
            class="form-label small fw-bold text-secondary"
          >
            {{ $t('Current Password') }}
          </label>
          <div class="custom-input-group input-group input-group-sm">
            <span class="input-group-text bg-white border-end-0 text-muted">
              <i class="fa-solid fa-key"></i>
            </span>
            <input
              v-model.trim="v$.form.current_password.$model"
              :type="showCurrent ? 'text' : 'password'"
              class="form-control border-start-0 border-end-0 ps-0"
              id="current_password"
              placeholder="••••••••"
              autocomplete="current-password"
            >
            <span
              class="input-group-text bg-white border-start-0 text-muted cursor-pointer"
              @click="showCurrent = !showCurrent"
            >
              <i class="far" :class="showCurrent ? 'fa-eye' : 'fa-eye-slash'"></i>
            </span>
          </div>
          <div
            v-for="error of v$.form.current_password.$errors"
            class="d-block invalid-feedback"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
          <div
            v-if="serverErrors.current_password"
            class="d-block invalid-feedback"
          >
            {{ $t(serverErrors.current_password) }}
          </div>
        </div>

        <div class="mb-3">
          <label
            for="new_password"
            class="form-label small fw-bold text-secondary"
          >
            {{ $t('New Password') }}
          </label>
          <div class="custom-input-group input-group input-group-sm">
            <span class="input-group-text bg-white border-end-0 text-muted">
              <i class="fa-solid fa-lock"></i>
            </span>
            <input
              v-model.trim="v$.form.new_password.$model"
              :type="showNew ? 'text' : 'password'"
              class="form-control border-start-0 border-end-0 ps-0"
              id="new_password"
              placeholder="••••••••"
              autocomplete="new-password"
            >
            <span
              class="input-group-text bg-white border-start-0 text-muted cursor-pointer"
              @click="showNew = !showNew"
            >
              <i class="far" :class="showNew ? 'fa-eye' : 'fa-eye-slash'"></i>
            </span>
          </div>
          <div
            v-for="error of v$.form.new_password.$errors"
            class="d-block invalid-feedback"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
          <div
            v-if="serverErrors.new_password"
            class="d-block invalid-feedback"
          >
            {{ $t(serverErrors.new_password) }}
          </div>
        </div>

        <div class="mb-4">
          <label
            for="new_password_confirmation"
            class="form-label small fw-bold text-secondary"
          >
            {{ $t('Confirm Password') }}
          </label>
          <div class="custom-input-group input-group input-group-sm">
            <span class="input-group-text bg-white border-end-0 text-muted">
              <i class="fa-solid fa-check-double"></i>
            </span>
            <input
              v-model.trim="v$.form.new_password_confirmation.$model"
              type="password"
              class="form-control border-start-0 ps-0"
              id="new_password_confirmation"
              placeholder="••••••••"
              autocomplete="new-password"
              @keyup.enter="handleSave"
            >
          </div>
          <div
            v-for="error of v$.form.new_password_confirmation.$errors"
            class="d-block invalid-feedback"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>

        <ladda
          text="Save Changes"
          :btn-class="'btn btn-theme w-100 py-2 fw-bold text-white shadow-sm mb-3'"
          :loading="btnLoading"
          icon="<i class='fa-solid fa-floppy-disk'></i>"
          :disabled="v$.$invalid"
          @click="handleSave"
        />

        <div class="text-center">
          <a
            class="small text-decoration-none text-muted"
            @click="handleLogout"
            style="cursor: pointer;"
          >
            <i class="fa-solid fa-right-from-bracket me-1" />{{ $t('Logout') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, requiredUnless, minLength, maxLength, sameAs, helpers } from '@vuelidate/validators';
import Ladda from '@/plugins/Ladda.vue';
import Cookies from 'js-cookie';
import { Auth } from '@/services/endpoint/auth-api.service';
import { useAuthStore } from '@/stores/auth.store';

/**
 * ตั้งรหัสผ่านใหม่ (api/docs/password-reset.md §5.2)
 *
 * เข้ามาได้ 2 ทาง:
 *  - โดน Owner รีเซ็ตรหัสให้ → router guard บังคับเด้งมาที่นี่ (forced = true) ไปหน้าอื่นไม่ได้จนกว่าจะตั้งใหม่
 *  - ผู้ใช้กดเข้ามาเปลี่ยนรหัสเอง (forced = false)
 */
export default {
  name: 'ChangePassword',
  components: {
    Ladda
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      btnLoading: false,
      failedMessage: '',
      serverErrors: {},
      showCurrent: false,
      showNew: false,
      form: {
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      }
    };
  },
  computed: {
    forced() {
      return !!useAuthStore().user?.must_change_password;
    }
  },
  validations() {
    return {
      form: {
        current_password: {
          // ตอนโดนบังคับตั้งรหัสใหม่ ไม่ถามรหัสเดิม → ไม่ต้องบังคับกรอก
          requiredUnless: helpers.withMessage(
            this.$t('Please enter current password'),
            requiredUnless(() => this.forced)
          )
        },
        new_password: {
          required: helpers.withMessage(this.$t('Please enter new password'), required),
          minLength: helpers.withMessage(
            ({ $params }) => this.$t('password must be at least') + ' ' + $params.min + ' ' + this.$t('characters long.'),
            minLength(8)
          ),
          maxLength: helpers.withMessage(
            ({ $params }) => this.$t('password cannot exceed') + ' ' + $params.max + ' ' + this.$t('characters.'),
            maxLength(16)
          )
        },
        new_password_confirmation: {
          required: helpers.withMessage(this.$t('Please confirm new password'), required),
          sameAsPassword: helpers.withMessage(this.$t('Passwords do not match.'), sameAs(this.form.new_password))
        }
      }
    };
  },
  methods: {
    async handleSave() {
      this.failedMessage = '';
      this.serverErrors = {};
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.btnLoading = true;
      try {
        // สร้าง payload ทีละ key — ระบบ reject key แปลกปลอม ห้าม clone ทั้งฟอร์ม (api/docs/CONVENTIONS.md §8)
        // ตอนโดนบังคับตั้งรหัสใหม่ ไม่ส่ง current_password เลย (ช่องถูกซ่อน BE ก็ไม่บังคับ)
        const payload = {
          new_password: this.form.new_password,
          new_password_confirmation: this.form.new_password_confirmation
        };
        if (!this.forced) payload.current_password = this.form.current_password;

        await Auth.changePassword(payload);

        // เคลียร์ธงในฝั่ง store ด้วย ไม่งั้น guard จะเด้งกลับมาหน้านี้อีก
        const authStore = useAuthStore();
        if (authStore.user) authStore.user = { ...authStore.user, must_change_password: false };

        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Your password has been changed'));
        this.$router.push('/');
      } catch (err) {
        const data = err?.response?.data;
        // FormValidationException → errors ราย field (api/docs/CONVENTIONS.md)
        if (data?.errors) {
          Object.keys(data.errors).forEach(key => {
            this.serverErrors[key] = data.errors[key][0];
          });
        } else {
          this.failedMessage = data?.message || 'Something went wrong';
        }
      } finally {
        this.btnLoading = false;
      }
    },
    async handleLogout() {
      // ท่าเดียวกับ TopMenu.logout — ออกจากระบบฝั่ง client ให้ได้เสมอ แม้ยิง logout ไม่ผ่าน
      try {
        await Auth.logout();
      } catch (e) {
      } finally {
        Cookies.remove('token');
        useAuthStore().logout();
        window.location.href = process.env.VUE_APP_URL + '/login';
      }
    }
  }
};
</script>
